import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AuthLayout, Field } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui-bits";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error(t("auth.reset.tooShort")); return; }
    if (pw !== confirm) { toast.error(t("auth.reset.mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success(t("auth.reset.success"));
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err, t("auth.reset.error")));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout title={t("auth.reset.invalidTitle")} subtitle={t("auth.reset.invalidSubtitle")}>
        <Link to="/forgot-password" data-testid="reset-request-new" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
          {t("auth.reset.requestNew")} <ArrowRight size={15} />
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.reset.title")} subtitle={t("auth.reset.subtitle")}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <Field label={t("auth.reset.newPassword")}>
          <Input data-testid="reset-password" type="password" required minLength={6} autoComplete="new-password" placeholder={t("auth.passwordHint")}
            value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 rounded-lg" />
        </Field>
        <Field label={t("auth.reset.confirmPassword")}>
          <Input data-testid="reset-confirm" type="password" required minLength={6} autoComplete="new-password" placeholder="••••••••"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 rounded-lg" />
        </Field>
        <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full h-11 rounded-lg active:scale-[0.98] transition-transform">
          {loading ? <Spinner /> : <>{t("auth.reset.cta")} <ArrowRight size={16} className="ml-1" /></>}
        </Button>
      </form>
      <Link to="/login" data-testid="back-to-login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
        <ArrowLeft size={15} /> {t("auth.forgot.backToLogin")}
      </Link>
    </AuthLayout>
  );
}
